import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import {
  Radio,
  Shield,
  ShieldCheck,
  Bot,
  Volume2,
  Bell,
  MapPin,
  Users,
  Download,
  EyeOff,
  Newspaper,
  Check,
  X,
  Zap,
  Crown,
  ArrowLeft,
  Loader2,
  Star,
  Building2,
  AlertTriangle,
  Cloud
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  radio: Radio,
  bot: Bot,
  volume: Volume2,
  bell: Bell,
  shield: Shield,
  newspaper: Newspaper,
  'map-pin': MapPin,
  users: Users,
  download: Download,
  'eye-off': EyeOff,
};

const PricingPage = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(null);
  const [currentTier, setCurrentTier] = useState('free');
  
  // Fetch pricing data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/compare`);
        if (res.ok) {
          const data = await res.json();
          setTiers(data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  // Get current user's tier
  useEffect(() => {
    if (token) {
      const fetchSubscription = async () => {
        try {
          const res = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setCurrentTier(data.tier);
          }
        } catch (err) {
          console.error('Failed to fetch subscription:', err);
        }
      };
      fetchSubscription();
    }
  }, [token]);
  
  const handleUpgrade = async (tierId) => {
    if (!token) {
      navigate('/auth?redirect=/pricing');
      return;
    }
    
    // Map tier to package ID
    const packageMap = {
      'basic': 'basic_monthly',
      'pro': 'pro_monthly',
      'business': 'business_monthly',
      'lifetime': 'lifetime'
    };
    
    const packageId = packageMap[tierId];
    if (!packageId) {
      toast({
        title: 'Invalid plan',
        description: 'Please select a valid plan.',
        variant: 'destructive'
      });
      return;
    }
    
    setUpgrading(tierId);
    
    try {
      const res = await fetch(`${API_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          toast({
            title: 'Error',
            description: 'No checkout URL received',
            variant: 'destructive'
          });
        }
      } else {
        const errorData = await res.json();
        toast({
          title: 'Upgrade failed',
          description: errorData.detail || 'Please try again.',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Upgrade failed:', err);
      toast({
        title: 'Upgrade failed',
        description: 'Please try again or contact support.',
        variant: 'destructive'
      });
    } finally {
      setUpgrading(null);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }
  
  const freeTier = tiers?.tiers?.find(t => t.id === 'free');
  const basicTier = tiers?.tiers?.find(t => t.id === 'basic');
  const proTier = tiers?.tiers?.find(t => t.id === 'pro');
  const businessTier = tiers?.tiers?.find(t => t.id === 'business');
  const lifetimeTier = tiers?.tiers?.find(t => t.id === 'lifetime');
  
  const tierOrder = ['free', 'basic', 'pro', 'business', 'lifetime'];
  const currentTierIndex = tierOrder.indexOf(currentTier);
  
  const getTierDisplayName = (tier) => {
    const names = {
      'free': 'Free Scout',
      'basic': 'Basic',
      'pro': 'Pro',
      'business': 'Business',
      'lifetime': 'Lifetime Founder'
    };
    return names[tier] || tier;
  };
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-slate-400 hover:text-white">Back to Home</span>
            </Link>
            {user && (
              <Badge variant="outline" className="border-slate-600">
                Current plan: {getTierDisplayName(currentTier)}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Hero */}
      <div className="text-center py-12 px-4">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Your Personal Intelligence<br />
          <span className="text-teal-400">Operations Center</span>
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-6">
          For less than the cost of a tank of gas, get a 24/7 intelligence team watching your back.
        </p>
        
        {/* New features highlight */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full text-sm">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span>Sex Offender Registry</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full text-sm">
            <Cloud className="w-4 h-4 text-yellow-400" />
            <span>Weather Alerts</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full text-sm">
            <Shield className="w-4 h-4 text-red-500" />
            <span>Terrorism Advisories</span>
          </div>
        </div>
      </div>
      
      {/* Pricing cards */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          
          {/* Free Tier */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 flex flex-col" data-testid="tier-free">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-slate-400" />
                <h3 className="text-lg font-bold">Scout</h3>
              </div>
              <p className="text-slate-400 text-sm">Get started free</p>
            </div>
            
            <div className="mb-4">
              <div className="text-3xl font-bold">$0</div>
              <div className="text-slate-400 text-sm">Free forever</div>
            </div>
            
            <ul className="space-y-2 mb-6 flex-grow text-sm">
              {freeTier?.features_list?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  {feature.included ? (
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
                  )}
                  <span className={feature.included ? 'text-slate-300' : 'text-slate-600'}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
            
            {currentTier === 'free' ? (
              <Button variant="outline" className="w-full border-slate-600" disabled>
                Current Plan
              </Button>
            ) : (
              <Link to="/dashboard">
                <Button variant="outline" className="w-full border-slate-600">
                  Get Started
                </Button>
              </Link>
            )}
          </div>
          
          {/* Basic Tier */}
          <div className="bg-slate-900/50 border border-blue-700/50 rounded-2xl p-5 flex flex-col" data-testid="tier-basic">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-bold">Basic</h3>
              </div>
              <p className="text-slate-400 text-sm">Full access to essentials</p>
            </div>
            
            <div className="mb-4">
              <div className="text-3xl font-bold">$12</div>
              <div className="text-slate-400 text-sm">per month</div>
            </div>
            
            <ul className="space-y-2 mb-6 flex-grow text-sm">
              {basicTier?.features_list?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-blue-400' : 'text-emerald-400'}`} />
                  <span className={feature.highlight ? 'text-white font-medium' : 'text-slate-300'}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
            
            {currentTierIndex >= tierOrder.indexOf('basic') ? (
              <Button className="w-full bg-blue-600/50" disabled>
                {currentTier === 'basic' ? 'Current Plan' : 'Included'}
              </Button>
            ) : (
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => handleUpgrade('basic')}
                disabled={upgrading === 'basic'}
                data-testid="upgrade-basic-btn"
              >
                {upgrading === 'basic' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Go Basic - $12/mo
              </Button>
            )}
          </div>
          
          {/* Pro Tier - Highlighted */}
          <div className="bg-gradient-to-b from-teal-900/30 to-slate-900/50 border-2 border-teal-500 rounded-2xl p-5 flex flex-col relative" data-testid="tier-pro">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-teal-500 text-white px-3">
                <Star className="w-3 h-3 mr-1" />
                Most Popular
              </Badge>
            </div>
            
            <div className="mb-4 mt-2">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-teal-400" />
                <h3 className="text-lg font-bold">Pro</h3>
              </div>
              <p className="text-slate-400 text-sm">Unlimited AI + priority</p>
            </div>
            
            <div className="mb-4">
              <div className="text-3xl font-bold">$16</div>
              <div className="text-slate-400 text-sm">per month</div>
            </div>
            
            <ul className="space-y-2 mb-6 flex-grow text-sm">
              {proTier?.features_list?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-teal-400' : 'text-emerald-400'}`} />
                  <span className={feature.highlight ? 'text-white font-medium' : 'text-slate-300'}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
            
            {currentTierIndex >= tierOrder.indexOf('pro') ? (
              <Button className="w-full bg-teal-600/50" disabled>
                {currentTier === 'pro' ? 'Current Plan' : 'Included'}
              </Button>
            ) : (
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading === 'pro'}
                data-testid="upgrade-pro-btn"
              >
                {upgrading === 'pro' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Go Pro - $16/mo
              </Button>
            )}
          </div>
          
          {/* Business Tier */}
          <div className="bg-gradient-to-b from-purple-900/20 to-slate-900/50 border border-purple-700/50 rounded-2xl p-5 flex flex-col" data-testid="tier-business">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold">Business</h3>
              </div>
              <p className="text-slate-400 text-sm">For teams & organizations</p>
            </div>
            
            <div className="mb-4">
              <div className="text-3xl font-bold">$40</div>
              <div className="text-slate-400 text-sm">per month</div>
            </div>
            
            <ul className="space-y-2 mb-6 flex-grow text-sm">
              {businessTier?.features_list?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-purple-400' : 'text-emerald-400'}`} />
                  <span className={feature.highlight ? 'text-purple-200 font-medium' : 'text-slate-300'}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
            
            {currentTierIndex >= tierOrder.indexOf('business') ? (
              <Button className="w-full bg-purple-600/50" disabled>
                {currentTier === 'business' ? 'Current Plan' : 'Included'}
              </Button>
            ) : (
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => handleUpgrade('business')}
                disabled={upgrading === 'business'}
                data-testid="upgrade-business-btn"
              >
                {upgrading === 'business' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Go Business - $40/mo
              </Button>
            )}
          </div>
        </div>
        
        {/* Lifetime Founder - Full Width */}
        <div className="mt-8 bg-gradient-to-r from-amber-900/20 via-slate-900/50 to-amber-900/20 border border-amber-700/50 rounded-2xl p-6" data-testid="tier-lifetime">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Crown className="w-8 h-8 text-amber-400" />
                <div>
                  <h3 className="text-2xl font-bold">Lifetime Founder</h3>
                  <p className="text-slate-400">Pay once, prepared forever. No monthly bills when SHTF.</p>
                </div>
              </div>
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4 text-sm">
                {lifetimeTier?.features_list?.slice(0, 6).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-amber-400" />
                    <span className="text-slate-300">{feature.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center md:text-right">
              <div className="text-4xl font-bold text-amber-400">$299</div>
              <div className="text-slate-400 text-sm mb-4">one-time payment</div>
              {currentTier === 'lifetime' ? (
                <Button className="bg-amber-600/50 px-8" disabled>
                  You're a Founder!
                </Button>
              ) : (
                <Button 
                  className="bg-amber-600 hover:bg-amber-700 px-8"
                  onClick={() => handleUpgrade('lifetime')}
                  disabled={upgrading === 'lifetime'}
                >
                  {upgrading === 'lifetime' ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Crown className="w-4 h-4 mr-2" />
                  )}
                  Become a Founder
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-8 mt-12 text-slate-400 text-sm">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Cancel anytime
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Secure Stripe payment
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            30-day money back
          </div>
        </div>
        
        {/* Feature comparison */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">What's New</h2>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
              <AlertTriangle className="w-8 h-8 text-red-400 mb-3" />
              <h3 className="font-semibold mb-2">Sex Offender Registry</h3>
              <p className="text-sm text-slate-400">View registered offenders in your area directly on the map. Data from public state registries.</p>
              <Badge className="mt-3 bg-blue-600/20 text-blue-300">Basic+</Badge>
            </div>
            <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
              <Cloud className="w-8 h-8 text-yellow-400 mb-3" />
              <h3 className="font-semibold mb-2">Weather Alerts</h3>
              <p className="text-sm text-slate-400">Real-time severe weather warnings from the National Weather Service.</p>
              <Badge className="mt-3 bg-emerald-600/20 text-emerald-300">All Plans</Badge>
            </div>
            <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
              <Shield className="w-8 h-8 text-red-500 mb-3" />
              <h3 className="font-semibold mb-2">Terrorism Advisories</h3>
              <p className="text-sm text-slate-400">DHS threat level bulletins and FBI alerts for your watch zones.</p>
              <Badge className="mt-3 bg-blue-600/20 text-blue-300">Basic+</Badge>
            </div>
          </div>
        </div>
        
        {/* FAQ */}
        {tiers?.faq && (
          <div className="mt-16 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {tiers.faq.map((item, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="bg-slate-900/50 border border-slate-800 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
        
        {/* Final CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 mb-4">Ready to get started?</p>
          <Link to="/auth">
            <Button variant="outline" className="border-slate-600">
              Create Free Account
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-slate-400 text-sm">
          <p>VeriWatch - Your Personal Intelligence Operations Center</p>
          <p className="mt-2">No hidden fees. No corporate BS. Just preppers helping preppers.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
